import { Box, IconButton, Paper, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { ErrorOutline } from '@mui/icons-material';
import { formatPriceParts } from "../payout-dashboard/PayoutDashboardHelper";

export const noData = () => (
  <Box sx={{ p: 2, border: 1, borderColor: 'grey.300', borderTop: 'none', borderRadius: '0 0 4px 4px' }}>
    <Typography color="text.secondary">No Data</Typography>
  </Box>
)

export const tableTitle = (title: string, isTableOpen: boolean, setIsTableOpen: React.Dispatch<React.SetStateAction<boolean>>) => (
  <Paper component={Box} elevation={3} onClick={() => setIsTableOpen(prev => !prev)}
    sx={{
      p: 2, backgroundColor: 'primary.dark', color: 'white', cursor: 'pointer',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
    }}>
    <Typography variant="h6">{title}</Typography>
    <IconButton color="inherit" size="small">
      {isTableOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
    </IconButton>
  </Paper>
);

export const tableHead = (header: string[], alignments?: ("left" | "center" | "right")[]) => (
  <TableHead>
    <TableRow sx={{ backgroundColor: 'primary.light' }}>
      {header.map((h, key) => (
        <TableCell
          key={key}
          align={alignments ? alignments[key] : "center"}
          sx={{ fontWeight: 'bold', color: 'white', whiteSpace: 'nowrap' }}
        >
          {h}
        </TableCell>
      ))}
    </TableRow>
  </TableHead>
)

export const balanceCell = (balance: number, currency: string) => {
  if (balance === -1) {
    return (
      <Tooltip arrow placement="top" title="The Lemonway API call failed to retrieve the balance.">
        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'help'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'warning.main', gap: 0.5 }}>
            <ErrorOutline sx={{ fontSize: 16 }} />
            <Typography variant="caption" sx={{ fontWeight: 600, textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
              Unavailable
            </Typography>
          </Box>
          <Box sx={{ minWidth: '25px' }} />
        </Box>
      </Tooltip>
    );
  }

  const { value, symbol } = formatPriceParts(balance, currency);

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'baseline',
      gap: 0.5,
      fontVariantNumeric: 'tabular-nums'
    }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: '25px', textAlign: 'left' }}>
        {symbol}
      </Typography>
    </Box>
  );
};

export const monitoringIcon = (isWarning: boolean, isApiError: boolean) => {
  if (isApiError) return <WarningIcon fontSize="small" sx={{ color: 'error.main' }} />;
  return isWarning
    ? <ErrorIcon fontSize="small" sx={{ color: 'warning.main' }} />
    : <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />;
};